import { useEffect, useRef } from 'react';
import type { ProductFeature as ProductFeatureSchema } from '@amplience/content-types/typings/c-product-feature';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { Locale } from '@storefront/lib-global/utils/internationalization/localeHelper';
import { FeatureContent } from './FeatureContent';
import { Link, ProductFeatureLink, ProductFeatureWrapper } from './ProductFeature.styles';

export type ProductFeatureTypes = { content: ProductFeatureSchema };

export type ProductFeatureProps = {
  subTechnology: ProductFeatureTypes;
};

type FeatureContentProps = ProductFeatureSchema;

export function ProductFeature({ subTechnology }: ProductFeatureProps) {
  const { locale } = useLocaleContext();
  const anchorLink = subTechnology?.content?.productFeatureAnchorLink;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    const isOutlet = locale === Locale.STOREFRONT_OUTLET_LOCALE;
    const link = anchorLink?.externalLink ?? anchorLink?.internalLink;
    const transformedLink = link === anchorLink?.externalLink ? link : `/${isOutlet ? '' : `${locale}/`}${link}`;

    const handleKeydown = (event: KeyboardEvent) => {
      const elementKey = event.target as HTMLElement;

      if (elementKey.tagName === 'A') return;

      if (event.key === 'Enter') {
        window.open(transformedLink, '_blank', 'noopener,noreferrer');
      }
    };

    const handleClick = (event: MouseEvent) => {
      const elementClick = event.target as HTMLElement;

      if (elementClick.tagName === 'A') return;

      window.open(transformedLink, '_blank', 'noopener,noreferrer');
    };

    if (link) {
      element?.addEventListener('click', handleClick);
      element?.addEventListener('keydown', handleKeydown);
    }

    return () => {
      element?.removeEventListener('click', handleClick);
      element?.removeEventListener('keydown', handleKeydown);
    };
  }, [ref, anchorLink, locale]);

  if (Object.keys(subTechnology.content || {}).length === 0) return null;

  const { productFeatureHeader, productFeatureImage, productFeatureRichText, productFeatureAnchorLink } =
    subTechnology.content;

  const featureProps = {
    productFeatureHeader,
    productFeatureRichText,
    productFeatureImage,
  };

  return (
    <ProductFeatureWrapper>
      {productFeatureAnchorLink ? (
        <ProductFeatureLink>
          <Link ref={ref} role="link" tabIndex={0} aria-label={`${productFeatureHeader?.text}`}>
            <FeatureContent {...(featureProps as FeatureContentProps)} />
          </Link>
        </ProductFeatureLink>
      ) : (
        <FeatureContent {...(featureProps as FeatureContentProps)} />
      )}
    </ProductFeatureWrapper>
  );
}

export default ProductFeature;
